import { useState } from 'react';
import { useInView } from '../hooks/useInView';
import { aboutData } from '../data/portfolioData';

export default function About() {
  const [ref, inView] = useInView(0.1);
  const [imgError, setImgError] = useState(false);

  return (
    <section id="about" ref={ref}>
      <div className="max-w-5xl mx-auto px-6 lg:px-16 py-24">
        <div className={`reveal ${inView ? 'visible' : ''}`}>
          <div className="section-eyebrow">01 — Who I am</div>
        </div>
        <h2
          className={`reveal ${inView ? 'visible' : ''} delay-1`}
          style={{ fontSize: 'clamp(2rem,5vw,3.2rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '0.75rem' }}
        >
          About Me
        </h2>
        <p
          className={`reveal ${inView ? 'visible' : ''} delay-2`}
          style={{ color: 'var(--text2)', fontSize: '1.05rem', maxWidth: 540, lineHeight: 1.7, marginBottom: '3.5rem' }}
        >
          A passionate engineer bridging frontend craft with data science research.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start text-justify">
          {/* Left: text + feature cards */}
          <div>
            {aboutData.description.map((p, i) => (
              <p
                key={i}
                className={`reveal ${inView ? 'visible' : ''} delay-${i + 3}`}
                style={{ color: 'var(--text2)', lineHeight: 1.8, marginBottom: '1.1rem', fontSize: '0.97rem' }}
              >
                {p}
              </p>
            ))}

            <div className="grid grid-cols-2 gap-3 mt-6">
              {aboutData.features.map((f, i) => (
                <div
                  key={f.title}
                  className={`reveal ${inView ? 'visible' : ''} delay-${i + 5}`}
                  style={{
                    padding: '1.15rem', borderRadius: 12,
                    border: '1px solid var(--border)', background: 'var(--card)',
                    transition: 'border-color 0.3s, transform 0.3s, background 0.3s',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,109,250,0.4)'; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.background = 'rgba(124,109,250,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = ''; e.currentTarget.style.background = 'var(--card)'; }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, marginBottom: '0.65rem',
                    background: 'rgba(124,109,250,0.12)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem',
                  }}>{f.icon}</div>
                  <h4 style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.3rem' }}>{f.title}</h4>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text2)', lineHeight: 1.5 }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: real profile photo + stats */}
          <div className={`reveal-right ${inView ? 'visible' : ''} delay-3 flex flex-col items-center gap-6`}>
            {/* Photo with spinning gradient ring */}
            <div style={{ position: 'relative', width: 260, height: 260 }}>
              {/* Spinning gradient ring */}
              <div style={{
                position: 'absolute', inset: -4, borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--accent), var(--accent2), var(--accent3))',
                animation: 'spin-slow 8s linear infinite', zIndex: 0,
              }} />
              {/* Inner bg gap */}
              <div style={{
                position: 'absolute', inset: 4, borderRadius: '50%',
                background: 'var(--bg)', zIndex: 1,
              }} />
              {/* Actual photo */}
              <div style={{
                position: 'absolute', inset: 8, borderRadius: '50%',
                overflow: 'hidden', zIndex: 2,
              }}>
                {!imgError ? (
                  <img
                    src={aboutData.profileImage}
                    alt="Guptaraj Shrestha"
                    onError={() => setImgError(true)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
                  />
                ) : (
                  /* Fallback initials if image fails */
                  <div style={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--bg3)',
                    fontFamily: "'Space Mono', monospace", fontSize: '3.5rem', fontWeight: 700,
                  }}>
                    <span className="gradient-text">GS</span>
                  </div>
                )}
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3 w-full">
              {aboutData.stats.map(s => (
                <div
                  key={s.label}
                  style={{
                    textAlign: 'center', padding: '1.1rem 0.75rem',
                    border: '1px solid var(--border)', borderRadius: 12,
                    background: 'var(--card)', transition: 'border-color 0.3s',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(124,109,250,0.4)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
                >
                  <span
                    className="gradient-text block"
                    style={{ fontFamily: "'Space Mono',monospace", fontSize: '1.7rem', fontWeight: 700 }}
                  >
                    {s.value}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text2)', letterSpacing: '0.05em', marginTop: 2, display: 'block' }}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
